/**
 * MathName — 사람이 친 이름(강의 행 이름) 속 인라인 수식($…$)만 KaTeX 로 그린다(오너 2026-09-13 「각 행에 수식이 처리되도록」).
 * 2026-10-20 부터 **개념 이름을 글자로 그리는 자리 전부**가 이것을 쓴다(오너 「모든 배지에서 수식 처리」) — ConceptBadge(13곳)·강의 조건
 * 칩·요약·해석 문장·문제 고르기 창·시점 문제 창·추이 툴팁·AI 트래커 표·추출 설정 표. 배지처럼 글씨가 작은 자리는 `[&_.katex]:text-[1em]`
 * 로 KaTeX 기본 1.21em 을 되돌린다(conceptBadge.jsx 참고). 개념 칩(ConceptChip)·K행렬·학교 시험 분석은 각자 renderLatex 를 쓴다.
 *
 * 행 이름은 사용자가 치는 글자라 개념 이름(사전 글자)과 다르게 다룬다.
 *   - 수식 밖 글자는 React 글자로만 넣는다 — HTML 로 해석하지 않는다. renderConceptLatex(ConceptChip)는 수식 밖을 HTML 로
 *     그대로 붙이므로 여기에 쓰면 `<img onerror=…>` 같은 이름이 화면에서 실행된다
 *   - 수식은 KaTeX 를 직접 부른다(react-katex 는 설정을 못 넘긴다). trust:false 로 \href · \url · \includegraphics · \html… 을 막고
 *     크기(maxSize) · 매크로 펼침(maxExpand)에 한도를 둔다. 한글이 섞여도 경고를 내지 않는다(strict:'ignore')
 *   - 깨진 수식은 그 조각의 원문을 글자로 보인다. `$` 가 하나뿐이면 수식이 아니다
 *   - compact — 좁은 자리(나무 · 메뉴 · 경로)는 \dfrac · \displaystyle 을 보통 크기로 그려 줄 높이를 고르게 둔다.
 *     보이는 모양만 바꾸고 저장된 원문은 그대로다(오너 2026-09-13 계획 D1)
 * 같은 수식은 한 번만 계산해 둔다 — 나무 행이 수백 개여도 다시 그릴 때마다 계산하지 않게.
 * 브라우저가 수식을 못 그리는 자리(title · aria-label · window.confirm · <option> · 입력칸)에는 쓰지 않는다 — 원문 그대로 둔다.
 */
import { Fragment, memo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css'; // 쓰는 파일이 각자 넣는 것이 이 저장소 관례다 — 빠지면 수식이 조용히 깨진다

const KATEX_OPTIONS = {
  displayMode: false, throwOnError: true, trust: false, strict: 'ignore', maxSize: 10, maxExpand: 100,
};
const CACHE_MAX = 500;
const cache = new Map();

/** 좁은 자리용 — 큰 분수 · display 크기를 보통 크기로(보이는 모양만) */
const compactTex = (tex) => tex
  .replace(/\\dfrac(?![a-zA-Z])/g, '\\frac')
  .replace(/\\displaystyle(?![a-zA-Z])/g, '\\textstyle');

/** 수식 하나 → KaTeX HTML. 못 그리면 null(부르는 쪽이 원문 글자를 보인다) */
export function renderMathHtml(tex, { compact = false } = {}) {
  const src = compact ? compactTex(tex) : tex;
  const key = `${compact ? 'c' : 'n'} ${src}`;
  if (cache.has(key)) return cache.get(key);
  let html = null;
  try {
    html = katex.renderToString(src, KATEX_OPTIONS);
  } catch (e) {
    html = null;
  }
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
  cache.set(key, html);
  return html;
}

/** 조각 나누기 — [{ text } | { tex, raw }]. `$…$` 짝만 수식 조각이다 */
export function splitMath(value) {
  const s = value == null ? '' : String(value);
  if (!s.includes('$')) return s ? [{ text: s }] : [];
  const parts = [];
  const re = /\$([^$]+)\$/g;
  let last = 0;
  let m = re.exec(s);
  while (m !== null) {
    if (m.index > last) parts.push({ text: s.slice(last, m.index) });
    parts.push({ tex: m[1], raw: m[0] });
    last = m.index + m[0].length;
    m = re.exec(s);
  }
  if (last < s.length) parts.push({ text: s.slice(last) });
  return parts;
}

function MathName({ text, compact = false }) {
  return (
    <>
      {splitMath(text).map((p, i) => {
        // 조각은 이름 안의 자리로만 구별된다 — 이름이 바뀌면 통째로 다시 그린다
        // eslint-disable-next-line react/no-array-index-key
        if (p.text != null) return <Fragment key={i}>{p.text}</Fragment>;
        const html = renderMathHtml(p.tex, { compact });
        // eslint-disable-next-line react/no-array-index-key
        if (html == null) return <Fragment key={i}>{p.raw}</Fragment>;
        // KaTeX 가 만든 HTML 만 넣는다(사람이 친 글자는 위 두 갈래에서 React 글자로 들어간다)
        // eslint-disable-next-line react/no-array-index-key, react/no-danger
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </>
  );
}

const MathNameMemo = memo(MathName);
export default MathNameMemo;

/**
 * 경로 — 「강의 › 상위 행 › …」. 사이는 「 › 」라 이어 붙인 글자와 읽히는 모양이 같다.
 * 첫 조각은 강의 제목이라 수식으로 그리지 않는다(이번 범위는 행 이름만, 오너 2026-09-13 계획 D3)
 */
export function MathPath({ names, plainFirst = true, compact = true }) {
  const list = Array.isArray(names) ? names : [];
  return (
    <>
      {list.map((n, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={i}>
          {i > 0 && ' › '}
          {i === 0 && plainFirst ? n : <MathNameMemo text={n} compact={compact} />}
        </Fragment>
      ))}
    </>
  );
}
